import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Pencil, Plus, X, MoreVertical, AlertCircle, Eye, Lock, Copy, Loader2, LogIn, Download } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

type User = {
  id: number;
  empCode?: string;
  firstName: string;
  lastName?: string;
  email: string;
  mobile: string;
  ivrMobile?: string;
  userType: string;
  branchId?: number;
  parentUserId?: number | null;
  parentUser?: { id: number; firstName?: string; lastName?: string } | null;
  branch?: { id: number; name: string };
  roleId?: number;
  role?: { id: number; name: string };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string | null;
};

/** Format a login timestamp in IST, or "Never" when the user has not logged in yet. */
function fmtLastLogin(v?: string | null): string {
  if (!v) return 'Never';
  const d = new Date(v);
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const USER_TYPES = [
  'SUPER_ADMIN','ADMIN','BRANCH_HEAD','OPERATION','BOOKING_INCHARGE','PICKUP',
  'FIELD_EXECUTIVE','DRIVER','OFFICE_BOY','SALES_PERSON','CUSTOMER_SUPPORT',
  'THIRD_PARTY','COMPANY_PAYROLL'
];

export function UsersPage() {
  const qc = useQueryClient();
  const me = useAuth((s) => s.user);
  const impersonate = useAuth((s) => s.impersonate);
  const canImpersonate = me?.userType === 'SUPER_ADMIN' || me?.userType === 'ADMIN';
  const [impBusy, setImpBusy] = useState<number | null>(null);

  const loginAs = async (u: User) => {
    if (!window.confirm(`Log in as ${u.firstName} ${u.lastName ?? ''}? You'll see the app with their permissions. Use "Exit" in the top bar to return to your admin account.`)) return;
    setImpBusy(u.id);
    try {
      await impersonate(u.id, `${u.firstName} ${u.lastName ?? ''}`.trim());
      window.location.href = '/dashboard';
    } catch (e: any) {
      const status = e?.response?.status;
      toast.error(
        status === 401
          ? 'Your session has expired — please log out and log back in, then try again.'
          : (e?.response?.data?.message ?? 'Could not log in as this user'),
      );
      setImpBusy(null);
    }
  };

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  // '' = any, 'none' = no role assigned, otherwise a role id.
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  const params = useMemo(() => {
    const p: any = { page, pageSize };
    if (search) p.q = search;
    if (statusFilter) p.isActive = statusFilter;
    if (branchFilter) p.branchId = branchFilter;
    if (typeFilter) p.userType = typeFilter;
    if (roleFilter) p.roleId = roleFilter;
    return p;
  }, [search, statusFilter, branchFilter, typeFilter, roleFilter, page, pageSize]);

  // Any filter change goes back to page 1 — staying on page 4 of a narrower result reads as
  // "no users found" when there are plenty on page 1.
  useEffect(() => { setPage(1); }, [search, statusFilter, branchFilter, typeFilter, roleFilter]);

  const { data: stats } = useQuery({
    queryKey: ['users-stats'],
    queryFn: async () => (await api.get('/users/stats')).data as { total: number; active: number; inactive: number; byType: any[]; byBranch: any[]; byRole: any[] },
  });

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['users', params],
    queryFn: async () => {
      const r = await api.get('/users', { params });
      const d: any = r.data;
      // Tolerate both shapes
      if (Array.isArray(d)) return { total: d.length, items: d };
      return { total: d?.total ?? 0, items: d?.items ?? [] };
    },
    // Resilience: retry transient failures (a courier-api restart or network blip briefly 5xx/fails),
    // and keep the previous page visible while refetching so the list never flickers to empty.
    retry: 2,
    retryDelay: (n) => Math.min(1000 * 2 ** n, 4000),
    placeholderData: keepPreviousData,
  });

  const { data: branches = [] } = useQuery({
    queryKey: ['branches-light'],
    queryFn: async () => {
      const r = await api.get('/branches');
      const d: any = r.data;
      if (Array.isArray(d)) return d;
      return d?.items ?? [];
    },
  });
  const branchList = Array.isArray(branches) ? branches : [];

  const { data: roles = [] } = useQuery({
    queryKey: ['roles-light'],
    queryFn: async () => {
      try {
        const r = await api.get('/roles');
        const d: any = r.data;
        if (Array.isArray(d)) return d;
        return d?.items ?? [];
      } catch { return []; }
    },
  });

  const total = (data as any)?.total ?? 0;
  const items: User[] = (data as any)?.items ?? [];
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Users</h1>
        <div className="flex items-center gap-2">
          {/* Exports WHAT IS ON SCREEN — the same filters, so the file and the list can never be
              two different sets of people. Page size is not sent: the export is the whole result. */}
          <button
            onClick={async () => {
              const qs = new URLSearchParams();
              Object.entries(params).forEach(([k, v]) => { if (k !== 'page' && k !== 'pageSize') qs.set(k, String(v)); });
              try {
                await downloadBlob(`/users/export?${qs.toString()}`, `users-${new Date().toISOString().slice(0, 10)}.csv`);
              } catch (e: any) {
                toast.error(e?.response?.status === 403
                  ? 'Only an admin can export the user list.'
                  : (e?.response?.data?.message ?? 'Could not export the users'));
              }
            }}
            className="px-4 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1">
            <Download size={14} /> Export
          </button>
          <button onClick={() => { setEditing(null); setShow(true); }} className="px-4 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1">
            <Plus size={14} /> Add User
          </button>
        </div>
      </div>
      <UsersStatsDashboard stats={stats} />
      <div className="grid grid-cols-12 gap-3 mb-2">
        <div className="col-span-12 md:col-span-4">
          <label className="text-xs text-gray-500">Name</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Enter Name Here" className="w-full border rounded px-2 py-1 text-sm" />
        </div>
        {/* User Type is the account's own kind (what the JWT carries); Role is the permission set
            assigned to it. They are different things and staff filter by both, so both are here. */}
        <div className="col-span-6 md:col-span-2">
          <label className="text-xs text-gray-500">User Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
            <option value="">All</option>
            {USER_TYPES.map((u) => <option key={u} value={u}>{u.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div className="col-span-6 md:col-span-2">
          <label className="text-xs text-gray-500">Role</label>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
            <option value="">All</option>
            <option value="none">— No role —</option>
            {(roles ?? []).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div className="col-span-6 md:col-span-2">
          <label className="text-xs text-gray-500">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <div className="col-span-6 md:col-span-2">
          <label className="text-xs text-gray-500">Select Branches</label>
          <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value ? Number(e.target.value) : '')} className="w-full border rounded px-2 py-1 text-sm">
            <option value="">All</option>
            {branchList.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>
      {(search || typeFilter || roleFilter || statusFilter || branchFilter) && (
        <div className="mb-3">
          <button
            onClick={() => { setSearch(''); setTypeFilter(''); setRoleFilter(''); setStatusFilter(''); setBranchFilter(''); }}
            className="text-xs text-emerald-700 border border-emerald-200 rounded px-2 py-1 hover:bg-emerald-50">
            Clear filters
          </button>
        </div>
      )}
      <div className="text-xs mb-2 text-gray-500">{items.length} of {total}</div>
      <div className="overflow-x-auto bg-white border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50">
            <tr>
              <th className="px-2 py-2 text-left">EMP Id</th>
              <th className="px-2 py-2 text-left">EMP Code</th>
              <th className="px-2 py-2 text-left">USER Type</th>
              <th className="px-2 py-2 text-left">Name</th>
              <th className="px-2 py-2 text-left">Email</th>
              <th className="px-2 py-2 text-left">Mobile</th>
              <th className="px-2 py-2 text-left">IVR Mobile</th>
              <th className="px-2 py-2 text-left">Created At</th>
              <th className="px-2 py-2 text-left">Updated At</th>
              <th className="px-2 py-2 text-left">Last Login</th>
              <th className="px-2 py-2 text-left">Branch Name</th>
              <th className="px-2 py-2 text-center">Status</th>
              <th className="px-2 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={13} className="text-center py-6 text-gray-400">Loading…</td></tr>
            ) : isError && items.length === 0 ? (
              <tr><td colSpan={13} className="text-center py-6">
                <div className="text-rose-600 mb-2">Couldn't load users (network/server hiccup).</div>
                <button onClick={() => refetch()} disabled={isFetching}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50">
                  {isFetching ? 'Retrying…' : 'Retry'}
                </button>
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={13} className="text-center py-6 text-gray-400">No users yet</td></tr>
            ) : (
              items.map((u) => (
                <tr key={u.id} className="border-t">
                  <td className="px-2 py-2">{u.id}</td>
                  <td className="px-2 py-2 font-mono text-xs">
                    {u.empCode ? (
                      <span className="inline-flex items-center gap-1">
                        {u.empCode}
                        <button
                          title="Copy EMP Code"
                          className="text-gray-400 hover:text-emerald-700"
                          onClick={() => { navigator.clipboard.writeText(u.empCode!); toast.success('EMP Code copied'); }}
                        ><Copy size={11} /></button>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-2 text-xs">{u.userType.replace(/_/g, ' ')}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-emerald-700 text-white flex items-center justify-center text-xs">{u.firstName?.[0]}</div>
                      <span>{u.firstName} {u.lastName ?? ''}</span>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center gap-1 text-xs">
                      <a href={`mailto:${u.email}`} className="text-emerald-700 hover:underline">{u.email}</a>
                      <button
                        title="Copy Email"
                        className="text-gray-400 hover:text-emerald-700"
                        onClick={() => { navigator.clipboard.writeText(u.email); toast.success('Email copied'); }}
                      ><Copy size={11} /></button>
                    </span>
                  </td>
                  <td className="px-2 py-2">{u.mobile}</td>
                  <td className="px-2 py-2">{u.ivrMobile ?? 'NA'}</td>
                  <td className="px-2 py-2 text-xs">{u.createdAt?.slice(0, 10)}</td>
                  <td className="px-2 py-2 text-xs">{u.updatedAt?.slice(0, 10)}</td>
                  <td className={`px-2 py-2 text-xs ${u.lastLoginAt ? 'text-gray-700' : 'text-gray-400 italic'}`}>{fmtLastLogin(u.lastLoginAt)}</td>
                  <td className="px-2 py-2">{u.branch?.name ?? '—'}</td>
                  <td className="px-2 py-2 text-center">
                    {u.isActive ? <span className="text-emerald-600">●</span> : <span className="text-gray-400">●</span>}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1">
                      <button title="Edit" onClick={() => { setEditing(u); setShow(true); }} className="border rounded px-2 py-1 text-xs flex items-center gap-1"><Pencil size={12} /> Edit</button>
                      {canImpersonate && u.userType !== 'SUPER_ADMIN' && u.isActive && (
                        <button title="Log in as this user" disabled={impBusy === u.id} onClick={() => loginAs(u)}
                          className="border border-indigo-200 text-indigo-700 rounded px-2 py-1 text-xs flex items-center gap-1 hover:bg-indigo-50 disabled:opacity-50">
                          {impBusy === u.id ? <Loader2 size={12} className="animate-spin" /> : <LogIn size={12} />} Login as
                        </button>
                      )}
                      <button title="More" className="border rounded px-1 py-1"><MoreVertical size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-sm">
        <div>Rows per page: 50</div>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-30">{'<'}</button>
          <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
          <button disabled={page >= lastPage} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-30">{'>'}</button>
        </div>
      </div>
      {show && (
        <UserForm
          initial={editing}
          branches={branchList}
          roles={roles as any[]}
          onClose={() => { setShow(false); setEditing(null); }}
          onSaved={() => { setShow(false); setEditing(null); qc.invalidateQueries({ queryKey: ['users'] }); }}
        />
      )}
    </div>
  );
}

// Clean payload: strip empty strings, coerce branchId/roleId to number|undefined, drop empty password on edit
function buildPayload(form: any, isEdit: boolean): any {
  const out: any = {};
  const setIf = (k: string, v: any) => { if (v !== undefined && v !== null && v !== '') out[k] = v; };
  setIf('firstName', form.firstName?.trim());
  setIf('lastName', form.lastName?.trim());
  setIf('email', form.email?.trim());
  setIf('mobile', form.mobile?.trim());
  setIf('ivrMobile', form.ivrMobile?.trim());
  setIf('empCode', form.empCode?.trim());
  setIf('userType', form.userType);
  if (form.branchId !== '' && form.branchId != null) out.branchId = Number(form.branchId);
  if (form.roleId !== '' && form.roleId != null) out.roleId = Number(form.roleId);
  // Sent even when empty — `null` is how a manager is REMOVED, so this cannot go through `setIf`,
  // which strips empty values and would silently keep the old manager on screen after "— None —".
  out.parentUserId = form.parentUserId === '' || form.parentUserId == null ? null : Number(form.parentUserId);
  out.isActive = !!form.isActive;
  if (form.password && form.password.length >= 8) out.password = form.password;
  // Validate password on create
  if (!isEdit && !out.password) throw new Error('Password must be at least 8 characters');
  if (!isEdit && (!out.firstName || !out.email || !out.mobile)) throw new Error('First Name, Email and Mobile are required');
  return out;
}

function UserForm({ initial, branches, roles, onClose, onSaved }: any) {
  // Candidate managers. Everyone active is offered — a manager is defined by who reports to them,
  // not by their user type, so an Operations lead can own a mixed team. The user being edited is
  // removed because a person cannot report to themselves (the server refuses it too).
  const { data: managerOptions = [] } = useQuery({
    queryKey: ['users', 'assignable'],
    queryFn: async () => (await api.get('/users/assignable')).data as { id: number; name: string; empCode?: string; userType?: string }[],
    staleTime: 5 * 60 * 1000,
  });
  const [viewPwdOpen, setViewPwdOpen] = useState(false);
  const [adminPwd, setAdminPwd] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busyView, setBusyView] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
  const [form, setForm] = useState<any>({
    firstName: initial?.firstName ?? '',
    lastName: initial?.lastName ?? '',
    email: initial?.email ?? '',
    mobile: initial?.mobile ?? '',
    ivrMobile: initial?.ivrMobile ?? '',
    userType: initial?.userType ?? 'OPERATION',
    empCode: initial?.empCode ?? '',
    branchId: initial?.branchId ?? '',
    roleId: initial?.roleId ?? '',
    parentUserId: initial?.parentUserId ?? '',
    isActive: initial?.isActive ?? true,
    password: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [lookupId, setLookupId] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);

  const fetchEmployee = async () => {
    if (!lookupId.trim()) { toast.error('Enter an Employee ID first'); return; }
    setLookupBusy(true);
    try {
      const r = await api.get('/users/employee-lookup', { params: { employeeId: lookupId.trim() } });
      const d = r.data;
      setForm((f: any) => ({ ...f,
        firstName: d.firstName || f.firstName,
        lastName: d.lastName || f.lastName,
        email: d.email || f.email,
        mobile: d.mobile || f.mobile,
        empCode: d.empCode || f.empCode,
      }));
      toast.success(`Loaded ${d.firstName} ${d.lastName} (${d.employeeId})`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Employee not found');
    } finally { setLookupBusy(false); }
  };

  const doViewPassword = async () => {
    if (!adminPwd || !initial) return;
    setBusyView(true);
    try {
      const r = await api.post(`/users/${initial.id}/view-password`, { adminPassword: adminPwd });
      if (r.data?.available) {
        setRevealed(r.data.password);
        setTimeout(() => setRevealed(null), 60000);
        setViewPwdOpen(false);
        setAdminPwd('');
      } else {
        // Don't close the modal — let the user click "Reset & Reveal" with the same admin password
        setUnavailableReason(r.data?.reason ?? 'Password not retrievable.');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to retrieve password');
    } finally { setBusyView(false); }
  };

  const doResetAndReveal = async () => {
    if (!adminPwd || !initial) return;
    if (!confirm(`Generate a NEW password for ${initial.firstName} ${initial.lastName ?? ''} and reveal it once? This will replace their existing password — they will need to use the new one to log in.`)) return;
    setBusyView(true);
    try {
      const r = await api.post(`/users/${initial.id}/reset-and-reveal-password`, { adminPassword: adminPwd });
      if (r.data?.password) {
        setRevealed(r.data.password);
        setTimeout(() => setRevealed(null), 120000);
        setViewPwdOpen(false);
        setUnavailableReason(null);
        setAdminPwd('');
        toast.success('New password generated. Copy it now — it auto-hides in 2 minutes.');
      } else {
        toast.error('Reset failed');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to reset password');
    } finally { setBusyView(false); }
  };

  const save = useMutation({
    mutationFn: async () => {
      setErr(null);
      const payload = buildPayload(form, !!initial);
      if (initial) return (await api.patch(`/users/${initial.id}`, payload)).data;
      return (await api.post('/users', payload)).data;
    },
    onSuccess: () => { toast.success(initial ? 'User updated' : 'User created'); onSaved(); },
    onError: (e: any) => {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Failed to save user';
      const text = Array.isArray(msg) ? msg.join(', ') : String(msg);
      setErr(text); toast.error(text);
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[720px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between rounded-t">
          <div className="font-semibold">{initial ? 'Edit User' : 'Add User'}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {err && (
          <div className="mx-5 mt-4 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div>{err}</div>
          </div>
        )}
        <div className="p-5 grid grid-cols-2 gap-4">
          {form.userType === 'COMPANY_PAYROLL' && (
            <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded p-3">
              <div className="text-xs font-medium text-emerald-800 mb-1">Company Payroll — auto-fill from HR by Employee ID</div>
              <div className="flex items-center gap-2">
                <input value={lookupId} onChange={(e)=>setLookupId(e.target.value)} onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); fetchEmployee(); } }} placeholder="e.g. SHR900040" className="flex-1 border rounded px-2 py-1.5 text-sm font-mono" />
                <button type="button" disabled={lookupBusy} onClick={fetchEmployee} className="px-3 py-1.5 text-sm rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 inline-flex items-center gap-1">
                  {lookupBusy && <Loader2 size={14} className="animate-spin" />} Fetch details
                </button>
              </div>
              <div className="text-[11px] text-emerald-700 mt-1">Enter the employee's HR Employee ID and press Fetch to auto-fill name, email, mobile and EMP code.</div>
            </div>
          )}
          <Field label="First Name *"><input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Last Name"><input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Email *"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="Mobile *"><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="IVR Mobile"><input value={form.ivrMobile} onChange={(e) => setForm({ ...form, ivrMobile: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" /></Field>
          <Field label="EMP Code"><input value={form.empCode} onChange={(e) => setForm({ ...form, empCode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></Field>
          <Field label="User Type *">
            <select value={form.userType} onChange={(e) => setForm({ ...form, userType: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm">
              {USER_TYPES.map((u) => <option key={u} value={u}>{u.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
          <Field label="Role">
            <select value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value ? Number(e.target.value) : '' })} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">— None —</option>
              {(roles ?? []).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="Branch">
            <select value={form.branchId} onChange={(e) => setForm({ ...form, branchId: e.target.value ? Number(e.target.value) : '' })} className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">— None —</option>
              {branches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          {/* Reporting Manager drives who can see and assign this person's work. A CS agent with no
              manager set is invisible to their CS Manager on the ticket queue, so this is the field
              that has to be filled in before team-based assignment means anything. */}
          <Field label="Reporting Manager">
            <select value={form.parentUserId} onChange={(e) => setForm({ ...form, parentUserId: e.target.value ? Number(e.target.value) : '' })}
              className="w-full border rounded px-2 py-1.5 text-sm">
              <option value="">— None —</option>
              {managerOptions
                .filter((m) => !initial || m.id !== initial.id)
                .map((m) => <option key={m.id} value={m.id}>{m.name}{m.userType ? ` · ${m.userType.replace(/_/g, ' ')}` : ''}</option>)}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">Their manager sees and can assign this user's support tickets.</p>
          </Field>
          <Field label={initial ? 'New Password (leave blank to keep)' : 'Password * (min 8 chars)'}>
            <div className="flex items-center gap-2">
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="flex-1 border rounded px-2 py-1.5 text-sm" />
              {initial && (
                <button type="button" onClick={() => { setViewPwdOpen(true); setRevealed(null); }}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
                  title="Admin-only: reveal current password">
                  <Eye size={12} /> View
                </button>
              )}
            </div>
            {revealed && (
              <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono text-xs">
                {revealed}
                <button type="button" onClick={() => { navigator.clipboard.writeText(revealed); toast.success('Copied'); }}
                  className="ml-1 text-emerald-600 hover:text-emerald-800"><Copy size={11} /></button>
              </div>
            )}
          </Field>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            <label className="text-sm">Active</label>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Submit'}</button>
        </div>
      </div>
      {viewPwdOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setViewPwdOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-amber-100 text-amber-700 p-2 rounded-lg"><Lock size={18} /></div>
              <h3 className="text-lg font-semibold">View user password</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Enter <b>your</b> admin password to reveal <b>{initial?.firstName} {initial?.lastName ?? ''}</b>'s current password.
              The password will auto-hide after 60 seconds.
            </p>
            <input type="password" placeholder="your admin password" value={adminPwd} autoFocus
              onChange={(e) => setAdminPwd(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doViewPassword()}
              className="w-full px-3 py-2 border rounded-lg outline-none focus:border-emerald-500" />
            {unavailableReason && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <div className="font-medium mb-1">Can't reveal current password</div>
                <div>{unavailableReason}</div>
                <button type="button" onClick={doResetAndReveal} disabled={busyView || !adminPwd}
                  className="mt-2 px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-60 flex items-center gap-1">
                  {busyView ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Reset &amp; Reveal new password
                </button>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => { setViewPwdOpen(false); setAdminPwd(''); setUnavailableReason(null); }}
                className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button type="button" onClick={doViewPassword} disabled={busyView || !adminPwd}
                className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2">
                {busyView ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Reveal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function UsersStatsDashboard({ stats }: { stats?: { total: number; active: number; inactive: number; byType: any[]; byBranch: any[]; byRole: any[] } }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-12 gap-3 mb-4">
      <div className="col-span-3 bg-white border border-slate-200 rounded p-3">
        <div className="text-xs text-slate-500">Total Users</div>
        <div className="text-2xl font-semibold mt-1">{stats.total}</div>
        <div className="text-[10px] text-slate-400 mt-0.5">across all branches</div>
      </div>
      <div className="col-span-3 bg-emerald-50 border border-emerald-200 rounded p-3">
        <div className="text-xs text-emerald-700">Active</div>
        <div className="text-2xl font-semibold mt-1 text-emerald-700">{stats.active}</div>
        <div className="text-[10px] text-emerald-600 mt-0.5">{stats.total ? Math.round((stats.active / stats.total) * 100) : 0}% of total</div>
      </div>
      <div className="col-span-3 bg-rose-50 border border-rose-200 rounded p-3">
        <div className="text-xs text-rose-700">Inactive</div>
        <div className="text-2xl font-semibold mt-1 text-rose-700">{stats.inactive}</div>
        <div className="text-[10px] text-rose-600 mt-0.5">{stats.total ? Math.round((stats.inactive / stats.total) * 100) : 0}% of total</div>
      </div>
      <div className="col-span-3 bg-indigo-50 border border-indigo-200 rounded p-3">
        <div className="text-xs text-indigo-700">User Types</div>
        <div className="text-2xl font-semibold mt-1 text-indigo-700">{stats.byType.length}</div>
        <div className="text-[10px] text-indigo-600 mt-0.5">distinct roles in use</div>
      </div>

      <div className="col-span-6 bg-white border border-slate-200 rounded p-3">
        <div className="text-xs font-semibold text-slate-600 mb-2">Users by Type</div>
        <div className="flex flex-wrap gap-1.5">
          {stats.byType.map(t => (
            <span key={t.userType} className="text-xs bg-slate-100 px-2 py-1 rounded inline-flex items-center gap-1.5">
              {t.userType.replace(/_/g,' ')}
              <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold">{t.count}</span>
            </span>
          ))}
        </div>
        {stats.byRole.length > 0 && (
          <>
            <div className="text-xs font-semibold text-slate-600 mt-3 mb-2">By Role</div>
            <div className="flex flex-wrap gap-1.5">
              {stats.byRole.slice(0, 10).map(r => (
                <span key={r.roleId} className="text-xs bg-amber-50 text-amber-800 px-2 py-1 rounded inline-flex items-center gap-1.5">
                  {r.roleName}
                  <span className="bg-amber-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-semibold">{r.count}</span>
                </span>
              ))}
              {stats.byRole.length > 10 && <span className="text-xs text-slate-400">+{stats.byRole.length - 10} more</span>}
            </div>
          </>
        )}
      </div>

      <div className="col-span-6 bg-white border border-slate-200 rounded p-3">
        <div className="text-xs font-semibold text-slate-600 mb-2">Users by Branch</div>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {stats.byBranch.slice(0, 10).map((b, i) => {
            const pct = stats.total ? (b.count / stats.total) * 100 : 0;
            return (
              <div key={b.branchId ?? `null-${i}`} className="flex items-center gap-2 text-xs">
                <span className="w-44 truncate text-slate-700">{b.branchName}</span>
                <div className="flex-1 bg-slate-100 h-2 rounded overflow-hidden">
                  <div className="bg-indigo-500 h-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-right font-semibold text-slate-700">{b.count}</span>
              </div>
            );
          })}
          {stats.byBranch.length > 10 && <div className="text-xs text-slate-400 mt-1">+{stats.byBranch.length - 10} more branches</div>}
        </div>
      </div>
    </div>
  );
}
